import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Ticket } from 'lucide-react';
import { ApiError, catalog, coupons, type CouponDraftPayload, type StripeCoupon } from '../api/index.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';

const fieldCls = 'mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';

type DiscountType = 'percent' | 'amount';
type Duration = 'once' | 'repeating' | 'forever';

interface Picked { id: string; name: string }

function cents(dollars: string): number | null {
  const trimmed = dollars.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function priceOrNull(value: string): number | null | 'bad' {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return 'bad';
  const cents = Math.round(n * 100);
  if (Math.abs(n * 100 - cents) > 1e-6) return 'bad';
  return cents / 100;
}

function countOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

function dateLabel(unix: number | null): string {
  if (!unix) return 'none';
  return new Date(unix * 1000).toISOString().slice(0, 10);
}

function money(centsValue: number | null, currency: string | null): string {
  if (centsValue == null) return '';
  return `${(centsValue / 100).toFixed(2)} ${(currency ?? 'usd').toUpperCase()}`;
}

function discountLabel(coupon: StripeCoupon): string {
  if (coupon.percentOff != null) return `${coupon.percentOff}% off`;
  return `${money(coupon.amountOff, coupon.currency)} off`;
}

function durationLabel(coupon: StripeCoupon): string {
  if (coupon.duration === 'forever') return 'forever';
  if (coupon.duration === 'repeating') return `${coupon.durationInMonths ?? '?'} months`;
  return 'once';
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

export function Coupons() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [percentOff, setPercentOff] = useState('30');
  const [amountDollars, setAmountDollars] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [duration, setDuration] = useState<Duration>('once');
  const [months, setMonths] = useState('3');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [redeemBy, setRedeemBy] = useState('');
  const [code, setCode] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [promoMax, setPromoMax] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [firstTime, setFirstTime] = useState(false);
  const [minimumDollars, setMinimumDollars] = useState('');
  const [appliesToAll, setAppliesToAll] = useState(false);
  const [publisherIds, setPublisherIds] = useState<string[]>([]);
  const [items, setItems] = useState<Picked[]>([]);
  const [groups, setGroups] = useState<Picked[]>([]);
  const [includeBundles, setIncludeBundles] = useState(true);
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [listQuery, setListQuery] = useState('');
  const [listStatus, setListStatus] = useState<'all' | 'valid' | 'invalid'>('all');
  const [itemSearch, setItemSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [formError, setFormError] = useState('');
  const [created, setCreated] = useState<{ id: string; code: string; summary: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['coupons'], queryFn: coupons.list });
  const publishers = useQuery({ queryKey: ['catalog-publishers'], queryFn: catalog.publishers });

  const parsedMinPrice = priceOrNull(minPrice);
  const minPriceInvalid = parsedMinPrice === 'bad';

  const eligibility = useMemo(() => ({
    appliesToAll,
    publisherIds,
    itemIds: items.map((row) => row.id),
    groupIds: groups.map((row) => row.id),
    includeBundles,
    includeUnpublished,
    includeArchived,
    minPrice: typeof parsedMinPrice === 'number' ? parsedMinPrice : null,
  }), [appliesToAll, publisherIds, items, groups, includeBundles, includeUnpublished, includeArchived, parsedMinPrice]);

  const preview = useQuery({
    queryKey: ['coupon-preview', eligibility],
    queryFn: () => coupons.preview(eligibility),
    enabled: !minPriceInvalid && !appliesToAll && (publisherIds.length + items.length + groups.length > 0),
  });

  const [debouncedCustomer, setDebouncedCustomer] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCustomer(customerQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [customerQuery]);
  const customerHits = useQuery({
    queryKey: ['coupon-customers', debouncedCustomer],
    queryFn: () => coupons.customers(debouncedCustomer),
    enabled: debouncedCustomer.length >= 2 && !customerId,
  });

  const itemHits = useQuery({
    queryKey: ['coupon-items', itemSearch],
    queryFn: () => catalog.items({ search: itemSearch, limit: 8 }),
    enabled: itemSearch.trim().length >= 2,
  });
  const groupHits = useQuery({
    queryKey: ['coupon-groups', groupSearch],
    queryFn: () => catalog.groups(undefined, groupSearch.trim()),
    enabled: groupSearch.trim().length >= 2,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['coupons'] });

  const create = useMutation({
    mutationFn: (body: CouponDraftPayload) => coupons.create(body),
    onSuccess: (coupon) => {
      setFormError('');
      const made = coupon.codes.find((row) => row.active)?.code ?? coupon.codes[0]?.code ?? '';
      const products = coupon.appliesToAll ? 'all products' : `${coupon.productCount} products`;
      setCreated({
        id: coupon.id,
        code: made,
        summary: `${discountLabel(coupon)}, ${durationLabel(coupon)}, ${products}.`,
      });
      setCopied(false);
      setOpenId(coupon.id);
      setCode('');
      refresh();
    },
    onError: (err) => setFormError(errorMessage(err)),
  });

  function togglePublisher(id: string) {
    setPublisherIds((current) => current.includes(id) ? current.filter((row) => row !== id) : [...current, id]);
  }

  function buildDraft(): CouponDraftPayload | string {
    const trimmedName = name.trim();
    if (!trimmedName) return 'Name is required.';
    if (trimmedName.length > 40) return 'Name must be 40 characters or fewer.';
    const trimmedCode = code.trim();
    if (!/^[A-Za-z0-9-]+$/.test(trimmedCode)) return 'Code may only contain letters, digits, and dashes.';
    let discount: CouponDraftPayload['discount'];
    if (discountType === 'percent') {
      const n = Number(percentOff);
      if (!Number.isFinite(n) || n <= 0 || n > 100) return 'Percent off must be greater than 0 and at most 100.';
      discount = { type: 'percent', percentOff: n };
    } else {
      const amountOff = cents(amountDollars);
      if (amountOff == null) return 'Amount off must be greater than 0.';
      if (!/^[a-zA-Z]{3}$/.test(currency.trim())) return 'Currency must be a 3-letter code.';
      discount = { type: 'amount', amountOff, currency: currency.trim().toLowerCase() };
    }
    if (duration === 'repeating') {
      const n = Number(months);
      if (!Number.isInteger(n) || n < 1 || n > 120) return 'Repeating duration needs 1 to 120 months.';
    }
    if (!appliesToAll && publisherIds.length + items.length + groups.length === 0) {
      return 'Pick a publisher or title, or choose all products.';
    }
    const minimumAmount = cents(minimumDollars);
    if (minimumDollars.trim() && minimumAmount == null) return 'Minimum amount must be greater than 0.';
    if (minPriceInvalid) return 'Minimum price must be greater than 0, with at most 2 decimal places.';
    const limit = preview.data?.productLimit ?? 100;
    if (!appliesToAll && preview.data && preview.data.productIds.length > limit) {
      return `Stripe allows at most ${limit} products. This pick is ${preview.data.productIds.length}.`;
    }
    return {
      name: trimmedName,
      code: trimmedCode,
      discount,
      duration,
      durationInMonths: duration === 'repeating' ? Number(months) : null,
      maxRedemptions: countOrNull(maxRedemptions),
      redeemBy: redeemBy || null,
      appliesToAll,
      publisherIds,
      itemIds: items.map((row) => row.id),
      groupIds: groups.map((row) => row.id),
      includeBundles,
      includeUnpublished,
      includeArchived,
      minPrice: typeof parsedMinPrice === 'number' ? parsedMinPrice : null,
      customer: customerId || null,
      promoMaxRedemptions: countOrNull(promoMax),
      expiresAt: expiresAt || null,
      firstTimeTransaction: firstTime,
      minimumAmount,
      minimumAmountCurrency: minimumAmount != null ? currency.trim().toLowerCase() : null,
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const draft = buildDraft();
    if (typeof draft === 'string') {
      setFormError(draft);
      return;
    }
    if (list.data?.mode === 'live' && !window.confirm('This writes a live Stripe coupon. Create it?')) return;
    setFormError('');
    setCreated(null);
    create.mutate(draft);
  }

  const publisherNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const publisher of publishers.data?.publishers ?? []) names.set(publisher._id, publisher.value || publisher.key);
    return names;
  }, [publishers.data]);

  const visibleCoupons = useMemo(() => {
    const needle = listQuery.trim().toLowerCase();
    return (list.data?.coupons ?? []).filter((coupon) => {
      if (listStatus === 'valid' && !coupon.valid) return false;
      if (listStatus === 'invalid' && coupon.valid) return false;
      if (!needle) return true;
      const haystack = [
        coupon.name,
        coupon.id,
        discountLabel(coupon),
        durationLabel(coupon),
        coupon.appliesToAll ? 'all products' : '',
        ...coupon.sampleNames,
        ...coupon.publisherIds.map((id) => publisherNames.get(id) ?? ''),
        ...coupon.codes.flatMap((row) => [row.code, row.customerId ?? '']),
      ].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [list.data, listQuery, listStatus, publisherNames]);

  const mode = list.data?.mode;
  const missingKey = list.error instanceof ApiError && list.error.status === 503;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Ticket size={22} className="text-brand-600" /> Coupons
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Stripe coupons and promotion codes. Publishers and titles expand to the Stripe products on those catalog rows.
        </p>
      </div>

      {list.data?.warning && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {list.data.warning}
        </p>
      )}
      {mode === 'live' && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Live Stripe. Coupons apply to real charges.
        </p>
      )}
      {mode === 'test' && (
        <p className="mb-4 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">Test Stripe.</p>
      )}
      {missingKey && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          STRIPE_SECRET_KEY is not set on the server.
        </p>
      )}

      <Card className="mb-6">
        <CardHeader><CardTitle>New coupon</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm font-medium text-gray-700">
                Name on receipt
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className={fieldCls} placeholder="SBT 30% off" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Promotion code
                <input value={code} onChange={(e) => setCode(e.target.value)} className={fieldCls} placeholder="FRIEND30" />
              </label>
            </div>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Discount</h2>
              <div className="flex gap-2">
                <Button type="button" variant={discountType === 'percent' ? 'primary' : 'secondary'} onClick={() => setDiscountType('percent')}>Percent off</Button>
                <Button type="button" variant={discountType === 'amount' ? 'primary' : 'secondary'} onClick={() => setDiscountType('amount')}>Amount off</Button>
              </div>
              {discountType === 'percent' ? (
                <label className="block text-sm font-medium text-gray-700 max-w-xs">
                  Percent
                  <input value={percentOff} onChange={(e) => setPercentOff(e.target.value)} className={fieldCls} inputMode="decimal" />
                </label>
              ) : (
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <label className="text-sm font-medium text-gray-700">
                    Amount
                    <input value={amountDollars} onChange={(e) => setAmountDollars(e.target.value)} className={fieldCls} placeholder="5.00" />
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Currency
                    <input value={currency} onChange={(e) => setCurrency(e.target.value)} className={fieldCls} maxLength={3} />
                  </label>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Duration</h2>
              <div className="flex flex-wrap gap-2">
                {(['once', 'repeating', 'forever'] as Duration[]).map((value) => (
                  <Button key={value} type="button" variant={duration === value ? 'primary' : 'secondary'} onClick={() => setDuration(value)}>
                    {value}
                  </Button>
                ))}
              </div>
              {duration === 'repeating' && (
                <label className="block text-sm font-medium text-gray-700 max-w-xs">
                  Months
                  <input value={months} onChange={(e) => setMonths(e.target.value)} className={fieldCls} />
                </label>
              )}
              <p className="text-xs text-gray-500">Once is the right choice for a single checkout. Repeating and forever matter for subscriptions.</p>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm font-medium text-gray-700">
                Coupon max redemptions
                <input value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} className={fieldCls} placeholder="Unlimited" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Redeem by
                <input type="date" value={redeemBy} onChange={(e) => setRedeemBy(e.target.value)} className={fieldCls} />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Code max redemptions
                <input value={promoMax} onChange={(e) => setPromoMax(e.target.value)} className={fieldCls} placeholder="Unlimited" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Code expires
                <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={fieldCls} />
              </label>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Who can use the code</h2>
              <label className="block text-sm font-medium text-gray-700">
                Customer
                <input
                  value={customerId ? customerLabel : customerQuery}
                  onChange={(e) => {
                    setCustomerId('');
                    setCustomerLabel('');
                    setCustomerQuery(e.target.value);
                  }}
                  className={fieldCls}
                  placeholder="Search Stripe customers by name or email"
                />
              </label>
              {customerId && (
                <button type="button" className="text-xs text-brand-700 underline" onClick={() => { setCustomerId(''); setCustomerLabel(''); setCustomerQuery(''); }}>
                  Clear {customerLabel}
                </button>
              )}
              {!customerId && (customerHits.data?.customers.length ?? 0) > 0 && (
                <ul className="border border-gray-200 rounded-lg divide-y">
                  {customerHits.data!.customers.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        onClick={() => { setCustomerId(hit.id); setCustomerLabel(hit.label); setCustomerQuery(''); }}
                      >
                        {hit.label}
                        <span className="ml-2 text-gray-400">{hit.id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={firstTime} onChange={(e) => setFirstTime(e.target.checked)} />
                First-time order only
              </label>
              <label className="block text-sm font-medium text-gray-700 max-w-xs">
                Minimum order amount
                <input value={minimumDollars} onChange={(e) => setMinimumDollars(e.target.value)} className={fieldCls} placeholder="Optional" />
              </label>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Eligible products</h2>
              <div className="flex gap-2">
                <Button type="button" variant={!appliesToAll ? 'primary' : 'secondary'} onClick={() => setAppliesToAll(false)}>Publishers and titles</Button>
                <Button type="button" variant={appliesToAll ? 'primary' : 'secondary'} onClick={() => setAppliesToAll(true)}>All products</Button>
              </div>
              {!appliesToAll && (
                <>
                  <div className="max-h-40 overflow-auto rounded-lg border border-gray-200 p-2 space-y-1">
                    {(publishers.data?.publishers ?? []).map((publisher) => (
                      <label key={publisher._id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={publisherIds.includes(publisher._id)}
                          onChange={() => togglePublisher(publisher._id)}
                        />
                        {publisher.value || publisher.key}
                      </label>
                    ))}
                  </div>
                  <label className="block text-sm font-medium text-gray-700 max-w-xs">
                    Minimum price
                    <input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className={fieldCls} placeholder="9.99" inputMode="decimal" />
                  </label>
                  <p className="text-xs text-gray-500">
                    Blank includes every price. A value keeps titles and bundles at or above it. Hand-picked titles stay either way.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={includeBundles} onChange={(e) => setIncludeBundles(e.target.checked)} />
                      Include bundles
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={includeUnpublished} onChange={(e) => setIncludeUnpublished(e.target.checked)} />
                      Include unpublished
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
                      Include archived
                    </label>
                  </div>
                  <Picker
                    label="Add a title"
                    search={itemSearch}
                    onSearch={setItemSearch}
                    hits={(itemHits.data?.items ?? []).map((item) => ({ id: item._id, name: item.name }))}
                    picked={items}
                    onAdd={(row) => setItems((current) => current.some((item) => item.id === row.id) ? current : [...current, row])}
                    onRemove={(id) => setItems((current) => current.filter((item) => item.id !== id))}
                  />
                  <Picker
                    label="Add a bundle"
                    search={groupSearch}
                    onSearch={setGroupSearch}
                    hits={(groupHits.data?.groups ?? []).map((group) => ({
                      id: group._id,
                      name: group.seriesName ? `${group.seriesName} — ${group.name}` : group.name,
                    }))}
                    picked={groups}
                    onAdd={(row) => setGroups((current) => current.some((group) => group.id === row.id) ? current : [...current, row])}
                    onRemove={(id) => setGroups((current) => current.filter((group) => group.id !== id))}
                  />
                  <p className={`text-sm ${preview.data && preview.data.productIds.length > preview.data.productLimit ? 'text-red-600' : 'text-gray-600'}`}>
                    {minPriceInvalid && 'Minimum price must be greater than 0, with at most 2 decimal places.'}
                    {!minPriceInvalid && preview.isFetching && 'Counting products…'}
                    {!minPriceInvalid && preview.data && !preview.isFetching && (
                      `${preview.data.productIds.length} Stripe products (${preview.data.itemCount} titles, ${preview.data.bundleCount} bundles). ${
                        preview.data.productIds.length > preview.data.productLimit
                          ? `Over Stripe's limit of ${preview.data.productLimit}.`
                          : `Under Stripe's limit of ${preview.data.productLimit}.`
                      } ${preview.data.sampleNames.slice(0, 4).join(', ')}`
                    )}
                    {!minPriceInvalid && preview.error && errorMessage(preview.error)}
                  </p>
                </>
              )}
              <p className="text-xs text-gray-500">
                Stripe will not edit a coupon’s product list. Rebuilding creates a new coupon and moves the active codes onto it.
              </p>
            </section>

            {formError && <p className="text-sm text-red-600">{formError}</p>}
            {created && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-900">
                <p className="font-medium">Coupon created.</p>
                <p className="mt-1">
                  Code <span className="font-mono font-semibold">{created.code || 'saved'}</span> is on Stripe. {created.summary}
                  The code field was cleared so another click does not make a second code.
                </p>
                <p className="mt-1 font-mono text-xs text-green-800">{created.id}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {created.code && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        void navigator.clipboard.writeText(created.code).then(() => setCopied(true));
                      }}
                    >
                      {copied ? 'Copied' : 'Copy code'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setListQuery('');
                      setListStatus('all');
                      setOpenId(created.id);
                      window.setTimeout(() => {
                        document.getElementById(`coupon-${created.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 50);
                    }}
                  >
                    Show in list
                  </Button>
                </div>
              </div>
            )}
            <Button type="submit" loading={create.isPending} disabled={missingKey || minPriceInvalid || (!appliesToAll && !!preview.data && preview.data.productIds.length > preview.data.productLimit)}>
              Create coupon
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Stripe coupons</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
            <input
              value={listQuery}
              onChange={(event) => setListQuery(event.target.value)}
              placeholder="Search name, code, or a listed title"
              className="w-full md:max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <div className="flex gap-2">
              {(['all', 'valid', 'invalid'] as const).map((status) => (
                <Button key={status} type="button" size="sm" variant={listStatus === status ? 'primary' : 'secondary'} onClick={() => setListStatus(status)}>
                  {status === 'all' ? 'All' : status === 'valid' ? 'Valid' : 'Invalid'}
                </Button>
              ))}
            </div>
            {list.data && (
              <p className="text-sm text-gray-500">{visibleCoupons.length} of {list.data.coupons.length}</p>
            )}
          </div>
          {list.isLoading && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={16} /> Loading…</p>}
          {list.error && !missingKey && <p className="text-sm text-red-600">{errorMessage(list.error)}</p>}
          {list.data?.truncated && <p className="mb-3 text-xs text-gray-500">Showing the first page of Stripe results.</p>}
          <div className="space-y-3">
            {visibleCoupons.map((coupon) => (
              <CouponRow
                key={coupon.id}
                coupon={coupon}
                publisherNames={publisherNames}
                open={openId === coupon.id}
                marked={created?.id === coupon.id}
                onToggle={() => setOpenId(openId === coupon.id ? null : coupon.id)}
                onChanged={refresh}
              />
            ))}
            {list.data && list.data.coupons.length === 0 && <p className="text-sm text-gray-500">No coupons in this Stripe account.</p>}
            {list.data && list.data.coupons.length > 0 && visibleCoupons.length === 0 && (
              <p className="text-sm text-gray-500">No coupons match that search.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Picker({
  label, search, onSearch, hits, picked, onAdd, onRemove,
}: {
  label: string;
  search: string;
  onSearch: (value: string) => void;
  hits: Picked[];
  picked: Picked[];
  onAdd: (row: Picked) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">
        {label}
        <input value={search} onChange={(e) => onSearch(e.target.value)} className={fieldCls} placeholder="Search…" />
      </label>
      {hits.length > 0 && (
        <ul className="mt-1 border border-gray-200 rounded-lg divide-y">
          {hits.map((hit) => (
            <li key={hit.id}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => onAdd(hit)}>
                {hit.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {picked.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {picked.map((row) => (
            <li key={row.id} className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
              {row.name}
              <button type="button" onClick={() => onRemove(row.id)} className="text-gray-500">×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  );
}

function CouponRow({
  coupon, publisherNames, open, marked, onToggle, onChanged,
}: {
  coupon: StripeCoupon;
  publisherNames: Map<string, string>;
  open: boolean;
  marked: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const [rename, setRename] = useState(coupon.name ?? '');
  const [code, setCode] = useState('');
  const [customer, setCustomer] = useState('');
  const [promoMax, setPromoMax] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [firstTime, setFirstTime] = useState(false);
  const [minimumDollars, setMinimumDollars] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await work();
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function rebuild() {
    if (!window.confirm('Create a new Stripe coupon with the current publisher titles and move active codes onto it?')) return;
    const duration = coupon.duration === 'forever' || coupon.duration === 'repeating' ? coupon.duration : 'once';
    await run(() => coupons.reissue(coupon.id, {
      name: (coupon.name || 'Coupon').slice(0, 40),
      discount: coupon.amountOff != null && coupon.currency
        ? { type: 'amount', amountOff: coupon.amountOff, currency: coupon.currency }
        : { type: 'percent', percentOff: coupon.percentOff ?? 1 },
      duration,
      durationInMonths: coupon.durationInMonths,
      maxRedemptions: coupon.maxRedemptions,
      redeemBy: coupon.redeemBy ? new Date(coupon.redeemBy * 1000).toISOString().slice(0, 10) : null,
      appliesToAll: false,
      publisherIds: coupon.publisherIds,
      itemIds: [],
      groupIds: [],
      includeBundles: coupon.includeBundles,
      includeUnpublished: coupon.includeUnpublished,
      includeArchived: coupon.includeArchived,
      minPrice: coupon.minPrice,
    }));
  }

  const publisherLabel = coupon.publisherIds.length
    ? coupon.publisherIds.map((id) => publisherNames.get(id) ?? id).join(', ')
    : 'Not limited by publisher';
  const productLabel = coupon.appliesToAll
    ? 'All products'
    : `${coupon.productCount} products`;
  const useLabel = coupon.maxRedemptions != null
    ? `${coupon.timesRedeemed} of ${coupon.maxRedemptions}`
    : `${coupon.timesRedeemed}`;
  const activeCodes = coupon.codes.filter((row) => row.active);

  return (
    <div id={`coupon-${coupon.id}`} className={`rounded-lg border bg-white ${marked ? 'border-green-400 ring-2 ring-green-200' : 'border-gray-200'}`}>
      <div className="px-4 pt-4 pb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{coupon.name || 'Untitled coupon'}</h3>
            <Badge variant={coupon.valid ? 'success' : 'default'}>{coupon.valid ? 'Valid' : 'Invalid'}</Badge>
            {marked && <Badge variant="success">Just created</Badge>}
            {activeCodes.length > 0 && <Badge variant="info">{activeCodes.length} active {activeCodes.length === 1 ? 'code' : 'codes'}</Badge>}
          </div>
          <p className="mt-1 font-mono text-xs text-gray-400">{coupon.id}</p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={onToggle}>
          {open ? 'Hide tools' : 'Edit'}
        </Button>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 px-4 pb-4">
        <Fact label="Discount" value={discountLabel(coupon)} />
        <Fact label="Duration" value={durationLabel(coupon)} />
        <Fact label="Redeem by" value={coupon.redeemBy ? dateLabel(coupon.redeemBy) : 'No end date'} />
        <Fact label="Redemptions" value={useLabel} />
        <Fact label="Products" value={productLabel} />
        {coupon.minPrice != null && <Fact label="Min price" value={`$${coupon.minPrice.toFixed(2)}+`} />}
        <Fact label="Publishers" value={publisherLabel} />
      </dl>
      {coupon.sampleNames.length > 0 && (
        <p className="px-4 pb-4 text-sm text-gray-600">
          {coupon.sampleNames.join(' · ')}
          {coupon.productCount > coupon.sampleNames.length ? ` · +${coupon.productCount - coupon.sampleNames.length} more` : ''}
        </p>
      )}
      <div className="border-t border-gray-100 px-4 py-3">
        {coupon.codes.length === 0 && <p className="text-sm text-gray-500">No promotion codes on this coupon.</p>}
        {coupon.codes.length > 0 && (
          <ul className="space-y-2">
            {coupon.codes.map((row) => (
              <li key={row.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span className="font-mono font-medium text-gray-900">{row.code}</span>
                <span className={row.active ? 'text-green-700' : 'text-gray-400'}>{row.active ? 'Active' : 'Off'}</span>
                <span className="text-gray-500">{row.timesRedeemed}{row.maxRedemptions != null ? ` / ${row.maxRedemptions}` : ''} uses</span>
                {row.customerId && <span className="text-gray-500">{row.customerId}</span>}
                {row.firstTimeTransaction && <span className="text-gray-500">First order</span>}
                {row.minimumAmount != null && <span className="text-gray-500">Min {money(row.minimumAmount, row.minimumAmountCurrency)}</span>}
                {row.expiresAt != null && <span className="text-gray-500">Expires {dateLabel(row.expiresAt)}</span>}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => run(() => coupons.setCodeActive(row.id, !row.active))}
                >
                  {row.active ? 'Turn off' : 'Turn on'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm text-gray-700">
              Name
              <input value={rename} onChange={(e) => setRename(e.target.value)} className={fieldCls} maxLength={40} />
            </label>
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => run(() => coupons.rename(coupon.id, rename))}>
              Save name
            </Button>
            {coupon.publisherIds.length > 0 && (
              <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={rebuild}>
                Rebuild from publishers
              </Button>
            )}
          </div>
          <form
            className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
            onSubmit={(event) => {
              event.preventDefault();
              const minimumAmount = cents(minimumDollars);
              if (minimumDollars.trim() && minimumAmount == null) {
                setError('Minimum amount must be greater than 0.');
                return;
              }
              run(async () => {
                await coupons.addCode(coupon.id, {
                  code,
                  customer: customer.trim() || null,
                  promoMaxRedemptions: countOrNull(promoMax),
                  expiresAt: expiresAt || null,
                  firstTimeTransaction: firstTime,
                  minimumAmount,
                  minimumAmountCurrency: minimumAmount != null ? (coupon.currency ?? 'usd') : null,
                });
                setCode('');
              });
            }}
          >
            <label className="text-sm text-gray-700">
              Add code
              <input value={code} onChange={(e) => setCode(e.target.value)} className={fieldCls} placeholder="SUMMER30" />
            </label>
            <label className="text-sm text-gray-700">
              Customer id
              <input value={customer} onChange={(e) => setCustomer(e.target.value)} className={fieldCls} placeholder="cus_… optional" />
            </label>
            <label className="text-sm text-gray-700">
              Code max
              <input value={promoMax} onChange={(e) => setPromoMax(e.target.value)} className={fieldCls} placeholder="Unlimited" />
            </label>
            <label className="text-sm text-gray-700">
              Code expires
              <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={fieldCls} />
            </label>
            <label className="text-sm text-gray-700">
              Minimum amount
              <input value={minimumDollars} onChange={(e) => setMinimumDollars(e.target.value)} className={fieldCls} placeholder="Optional" />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input type="checkbox" checked={firstTime} onChange={(e) => setFirstTime(e.target.checked)} />
              First-time order
            </label>
            <Button type="submit" size="sm" disabled={busy || !code.trim()}>Add code</Button>
          </form>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
